import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Search, RefreshCw, LogIn, LogOut, UserX, AlertTriangle, WifiOff, DoorOpen } from "lucide-react";
import { staffApi, fmtTime, fmtErr } from "../api";
import { useAccess } from "../permissions";
import Timeline from "./Timeline";

// Attendance → Live Staff Monitor. Read-only browser view over the production
// attendance system (geofence tracking itself lives in the mobile app/backend).
// Aggregates: /monitor/live (canonical counters), /attendance/all (canonical daily
// records incl. total_hours), /attendance/events (today's immutable events),
// /locations/live (last GPS pins), /users + /branches (directory + geofence radii).
const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fmtDur = (mins) => {
  if (mins == null || isNaN(mins)) return "—";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};
const OFFLINE_AFTER_MIN = 60;

const PRESENCE = {
  inside: { label: "Inside", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  outside: { label: "Outside", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  left: { label: "Left for Day", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  not_arrived: { label: "Not Yet Arrived", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

const Card = ({ icon: Icon, label, value, testid, tone }) => (
  <div className="bg-white border border-slate-200 p-5" data-testid={testid}>
    <p className={`flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.18em] ${tone || "text-slate-500"}`}>
      <Icon size={13} /> {label}
    </p>
    <p className="mt-2 font-display text-3xl font-extrabold text-slate-900" data-testid={`${testid}-value`}>{value}</p>
  </div>
);

const LiveMonitor = () => {
  const access = useAccess();
  const [data, setData] = useState({ users: [], branches: [], records: [], events: [], pins: [], live: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [desig, setDesig] = useState("");
  const [branch, setBranch] = useState("");
  const [presence, setPresence] = useState("");
  const [selected, setSelected] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError("");
    const date = todayStr();
    try {
      const [u, b, r, e, p, l] = await Promise.allSettled([
        staffApi.get("/users"),
        staffApi.get("/branches"),
        staffApi.get("/attendance/all", { params: { date, limit: 1000 } }),
        staffApi.get("/attendance/events", { params: { date, limit: 1000 } }),
        staffApi.get("/locations/live"),
        staffApi.get("/monitor/live"),
      ]);
      setData({
        users: u.status === "fulfilled" && Array.isArray(u.value.data) ? u.value.data : [],
        branches: b.status === "fulfilled" && Array.isArray(b.value.data) ? b.value.data : [],
        records: r.status === "fulfilled" && Array.isArray(r.value.data) ? r.value.data : [],
        events: e.status === "fulfilled" && Array.isArray(e.value.data) ? e.value.data : [],
        pins: p.status === "fulfilled" && Array.isArray(p.value.data) ? p.value.data : [],
        live: l.status === "fulfilled" ? l.value.data : null,
      });
      if (u.status === "rejected") setError(fmtErr(u.reason));
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(() => load(true), 60000); // ~60s auto refresh
    return () => clearInterval(timer.current);
  }, [load]);

  const branchById = useMemo(() => Object.fromEntries(data.branches.map((b) => [b.id, b])), [data.branches]);
  const recordByUser = useMemo(() => Object.fromEntries(data.records.map((r) => [r.user_id, r])), [data.records]);
  const pinByUser = useMemo(() => Object.fromEntries(data.pins.map((p) => [p.user_id, p])), [data.pins]);
  const eventsByUser = useMemo(() => {
    const m = {};
    for (const e of data.events) (m[e.user_id] = m[e.user_id] || []).push(e);
    for (const k of Object.keys(m)) m[k].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return m;
  }, [data.events]);

  const rows = useMemo(() => {
    const now = Date.now();
    let list = data.users.map((u) => {
      const evs = eventsByUser[u.id] || [];
      const rec = recordByUser[u.id];
      const pin = pinByUser[u.id];
      const enters = evs.filter((e) => e.event_type === "enter");
      const exits = evs.filter((e) => e.event_type === "exit");
      const last = evs[evs.length - 1];
      let presenceKey = "not_arrived";
      if (last) presenceKey = last.event_type === "enter" ? "inside" : rec?.check_out_time ? "left" : "outside";
      const lastVerifiedTs = [pin?.updated_at, last?.timestamp].filter(Boolean).sort().pop();
      const stale = !lastVerifiedTs || (now - new Date(lastVerifiedTs).getTime()) / 60000 > OFFLINE_AFTER_MIN;
      const gpsException = evs.some((e) => {
        const br = e.branch_id ? branchById[e.branch_id] : null;
        return br && e.distance_m != null && e.distance_m > br.radius_m;
      }) || (pin && pin.distance_m != null && pin.branch_id && branchById[pin.branch_id] && pin.distance_m > branchById[pin.branch_id].radius_m);
      // outside during working = session gaps between exit and next entry
      let outsideMin = 0;
      for (let i = 0; i < evs.length - 1; i++) {
        if (evs[i].event_type === "exit" && evs[i + 1].event_type === "enter") outsideMin += (new Date(evs[i + 1].timestamp) - new Date(evs[i].timestamp)) / 60000;
      }
      return {
        user: u,
        presenceKey,
        firstIn: enters[0]?.timestamp || rec?.check_in_time,
        lastIn: enters.length ? enters[enters.length - 1].timestamp : null,
        lastOut: exits.length ? exits[exits.length - 1].timestamp : rec?.check_out_time,
        insideMin: rec?.total_hours != null ? Math.round(rec.total_hours * 60) : null, // canonical backend total
        outsideMin: evs.length ? outsideMin : null,
        entries: enters.length,
        exits: exits.length,
        branchName: pin?.branch_name || last?.branch_name || rec?.branch_name || branchById[u.primary_branch_id]?.name || "—",
        lastVerifiedTs,
        offline: stale,
        gpsException,
      };
    });
    // SuperAdmin privacy: hidden from every non-SuperAdmin viewer (defense-in-depth; backend is authoritative)
    if (!access.superadmin) list = list.filter((r) => r.user.role !== "SuperAdmin");
    return list;
  }, [data, eventsByUser, recordByUser, pinByUser, branchById, access.superadmin]);

  const departments = useMemo(() => [...new Set(data.users.map((u) => u.department).filter(Boolean))].sort(), [data.users]);
  const designations = useMemo(() => [...new Set(data.users.map((u) => u.designation).filter(Boolean))].sort(), [data.users]);

  const filtered = rows.filter((r) => {
    if (dept && r.user.department !== dept) return false;
    if (desig && r.user.designation !== desig) return false;
    if (branch && r.branchName !== branch) return false;
    if (presence && r.presenceKey !== presence) return false;
    const s = `${r.user.name} ${r.user.employee_code} ${r.user.designation} ${r.branchName}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const counts = useMemo(() => ({
    inside: data.live?.inside_count ?? rows.filter((r) => r.presenceKey === "inside").length,
    outside: data.live?.outside_count ?? rows.filter((r) => r.presenceKey === "outside").length,
    notArrived: data.live?.absent_today ?? rows.filter((r) => r.presenceKey === "not_arrived").length,
    left: rows.filter((r) => r.presenceKey === "left").length,
    gps: rows.filter((r) => r.gpsException).length,
    offline: rows.filter((r) => r.offline).length,
  }), [data.live, rows]);

  const branchNames = useMemo(() => [...new Set(rows.map((r) => r.branchName).filter((b) => b && b !== "—"))].sort(), [rows]);
  const selectCls = "border border-slate-300 px-3 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]";

  return (
    <div data-testid="live-monitor">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Live Staff Monitor</h1>
          <p className="mt-1 text-sm text-slate-500">
            Real-time presence from the geofence attendance system. Read-only in the browser.
            {lastUpdated && <span className="text-slate-400" data-testid="monitor-updated"> Updated {fmtTime(lastUpdated.toISOString())} · auto-refreshes every 60s</span>}
          </p>
        </div>
        <button onClick={() => load(true)} disabled={refreshing} data-testid="monitor-refresh"
          className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-[#C5221F] hover:text-[#C5221F] disabled:opacity-50 text-slate-700 px-4 py-2.5 text-[11px] font-display font-semibold uppercase tracking-widest rounded-sm transition-colors">
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="monitor-cards">
        <Card icon={LogIn} label="Currently Inside" value={counts.inside} testid="mc-inside" tone="text-emerald-600" />
        <Card icon={LogOut} label="Currently Outside" value={counts.outside} testid="mc-outside" tone="text-blue-600" />
        <Card icon={UserX} label="Not Yet Arrived" value={counts.notArrived} testid="mc-not-arrived" tone="text-amber-600" />
        <Card icon={DoorOpen} label="Left For Day" value={counts.left} testid="mc-left" />
        <Card icon={AlertTriangle} label="GPS Exceptions" value={counts.gps} testid="mc-gps" tone="text-amber-600" />
        <Card icon={WifiOff} label="Offline >60m" value={counts.offline} testid="mc-offline" tone="text-slate-500" />
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search employee, code, site…" value={q} data-testid="monitor-search"
            onChange={(e) => setQ(e.target.value)}
            className="w-full border border-slate-300 pl-9 pr-3 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" />
        </div>
        <select value={dept} onChange={(e) => setDept(e.target.value)} data-testid="monitor-filter-dept" className={selectCls}>
          <option value="">All Departments</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={desig} onChange={(e) => setDesig(e.target.value)} data-testid="monitor-filter-desig" className={selectCls}>
          <option value="">All Designations</option>
          {designations.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="monitor-filter-branch" className={selectCls}>
          <option value="">All Properties / Sites</option>
          {branchNames.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={presence} onChange={(e) => setPresence(e.target.value)} data-testid="monitor-filter-presence" className={selectCls}>
          <option value="">All Presence</option>
          {Object.entries(PRESENCE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading live presence…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="monitor-error">{error}</p>
      ) : filtered.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="monitor-empty">No staff match the current filters.</p>
      ) : (
        <div className="mt-5 bg-white border border-slate-200 overflow-x-auto" data-testid="monitor-table">
          <table className="w-full text-sm min-w-[1240px]">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                {["Employee", "Department", "Designation", "Property / Site", "Presence", "First In", "Latest In / Out", "Inside Today", "Outside", "Entries", "Exits", "Last Verified"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => {
                const p = PRESENCE[r.presenceKey];
                return (
                  <tr key={r.user.id} onClick={() => setSelected(r.user)} className="hover:bg-slate-50 cursor-pointer" data-testid={`monitor-row-${r.user.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{r.user.name}</p>
                      <p className="text-xs text-slate-400">{r.user.employee_code || "—"}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.user.department || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.user.designation || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.branchName}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${p.cls}`} data-testid={`monitor-presence-${r.user.id}`}>{p.label}</span>
                      {r.gpsException && (
                        <span className="ml-1.5 inline-block border border-amber-300 bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[9px] font-bold uppercase" title="GPS / Location Exception">GPS</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtTime(r.firstIn)}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtTime(r.lastIn)} / {fmtTime(r.lastOut)}</td>
                    <td className="px-4 py-3 font-medium text-slate-800" data-testid={`monitor-inside-${r.user.id}`}>{fmtDur(r.insideMin)}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDur(r.outsideMin)}</td>
                    <td className="px-4 py-3 text-slate-600">{r.entries}</td>
                    <td className="px-4 py-3 text-slate-600">{r.exits}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      <span className={r.offline ? "text-slate-400" : "text-emerald-600"} data-testid={`monitor-verified-${r.user.id}`}>{r.lastVerifiedTs ? fmtTime(r.lastVerifiedTs) : "—"}</span>
                      {r.offline && <span className="ml-1 text-[9px] font-bold uppercase text-slate-400">offline</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <Timeline user={selected} branches={data.branches} canCorrect={access.superadmin} onClose={() => { setSelected(null); load(true); }} />
      )}
    </div>
  );
};

export default LiveMonitor;
